import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";

// project imports
import NavItem from "../NavItem";
import RedBubble from "ui-component/RedBubble";
import { usePendingTasks } from "context/PendingTasksContext";

// assets
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { IconChevronDown, IconChevronUp } from "@tabler/icons";

// ==============================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||============================== //

const NavCollapse = ({ menu, level }) => {
  const theme = useTheme();
  const customization = useSelector((state) => state.customization);
  const { getCountForCollapse } = usePendingTasks();
  const collapsePendingCount = getCountForCollapse(menu);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const handleClick = () => {
    setOpen(!open);
    setSelected(!selected ? menu.id : null);
  };

  const { pathname } = useLocation();
  const checkOpenForParent = (child, id) => {
    child.forEach((item) => {
      if (item.url === pathname) {
        setOpen(true);
        setSelected(id);
      }
    });
  };

  // menu collapse for sub-levels
  useEffect(() => {
    setOpen(false);
    setSelected(null);
    if (menu.children) {
      menu.children.forEach((item) => {
        if (item.children?.length) {
          checkOpenForParent(item.children, menu.id);
        }
        if (item.url === pathname) {
          setSelected(menu.id);
          setOpen(true);
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, menu.children]);

  // menu collapse & item
  const menus = menu.children?.map((item) => {
    switch (item.type) {
      case "collapse":
        return <NavCollapse key={item.id} menu={item} level={level + 1} />;
      case "item":
        return <NavItem key={item.id} item={item} level={level + 1} />;
      default:
        return (
          <Typography
            key={item.id}
            variant="h6"
            color="error"
            align="center"
          ></Typography>
        );
    }
  });

  const Icon = menu.icon;
  const menuIcon = menu.icon ? (
    <Icon
      strokeWidth={1.5}
      size="1.3rem"
      style={{ marginTop: "auto", marginBottom: "auto" }}
    />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: selected === menu.id ? 8 : 6,
        height: selected === menu.id ? 8 : 6,
      }}
      fontSize={level > 0 ? "inherit" : "medium"}
    />
  );

  return (
    <>
      <ListItemButton
        sx={{
          borderRadius: "10px",
          mb: 0.75,
          alignItems: "center",
          backgroundColor: open ? "#e0e7ff" : level > 1 ? "transparent" : "inherit",
          color: open ? "#4338ca" : "#334155",
          boxShadow: open ? "0 4px 6px -1px rgba(67, 56, 202, 0.1), 0 2px 4px -1px rgba(67, 56, 202, 0.06)" : "none",
          transform: open ? "translateY(-2px)" : "none",
          py: level > 1 ? 1 : 1.15,
          px: 1.75,
          pl: level > 1 ? `${level * 20}px` : 1.75,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: open ? "#e0e7ff" : "#f1f5f9",
            color: open ? "#4338ca" : "#4f46e5",
            transform: open ? "translateY(-2px)" : "translateX(4px)",
            "& .MuiListItemIcon-root": {
              color: open ? "#4338ca" : menu?.color || "#4f46e5",
            },
          },
        }}
        selected={selected === menu.id}
        onClick={handleClick}
      >
        <ListItemIcon
          sx={{
            my: "auto",
            minWidth: !menu.icon ? 18 : 34,
            color: open ? "#4338ca" : menu?.color || "#64748b",
            transition: "color 0.3s ease",
          }}
        >
          {menuIcon}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              variant={selected === menu.id ? "h5" : "body1"}
              sx={{
                my: "auto",
                fontWeight: open || selected === menu.id ? 600 : 500,
                color: "inherit",
                fontSize: "0.875rem",
              }}
            >
              {menu.title}
            </Typography>
          }
          secondary={
            menu.caption && (
              <Typography
                variant="caption"
                sx={{ ...theme.typography.subMenuCaption }}
                display="block"
                gutterBottom
              >
                {menu.caption}
              </Typography>
            )
          }
        />
        {collapsePendingCount > 0 && (
          <RedBubble
            count={collapsePendingCount}
            tooltipText={`${collapsePendingCount} pending in ${menu.title}`}
            sx={{ ml: "auto", mr: 0.8 }}
          />
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            color: open ? "#4338ca" : "#94a3b8",
            ml: collapsePendingCount > 0 ? 0.2 : 1,
          }}
        >
          <IconChevronDown stroke={1.75} size="1.05rem" />
        </Box>
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List
          component="div"
          disablePadding
          sx={{
            position: "relative",
            ml: "24px",
            pl: "12px",
            my: 1,
            borderLeft: "2px solid transparent",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: "-2px",
              bottom: 0,
              width: "2px",
              backgroundColor: "#c7d2fe",
              borderRadius: "4px",
              transformOrigin: "top",
              animation: "expandLine 0.4s ease-out forwards",
            },
            "@keyframes expandLine": {
              "0%": { transform: "scaleY(0)" },
              "100%": { transform: "scaleY(1)" },
            },
            transition: "all 0.3s ease",
          }}
        >
          {menus}
        </List>
      </Collapse>
    </>
  );
};

NavCollapse.propTypes = {
  menu: PropTypes.object,
  level: PropTypes.number,
};

export default NavCollapse;
